import React from 'react';
import { matchPath, Navigate, useLocation } from 'react-router-dom';
import { useCustomDomain } from '../../utils/customDomain';
import TeamPortalPage from '../../pages/TeamPortalPage';

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
  </div>
);

/** "/" on an event's custom domain opens that event's registration form; elsewhere the platform home. */
export const DomainHome = ({ children }) => {
  const { status, site } = useCustomDomain();
  const location = useLocation();
  if (status === 'loading') return <Spinner />;
  if (status === 'event') {
    return <Navigate to={`/register/${site.slug || site.eventId}${location.search}`} replace />;
  }
  return children;
};

/** "/login" on a custom domain is the event's team sign-in; elsewhere the regular sign-in page. */
export const DomainLogin = () => {
  const { status, site } = useCustomDomain();
  if (status === 'loading') return <Spinner />;
  if (status === 'event') return <TeamPortalPage eventId={site.eventId} viaCustomDomain />;
  return <Navigate to="/auth" replace />;
};

// Pages that aren't tied to one event and are fine on an organiser's domain.
const SHARED_PATHS = ['/', '/login', '/my-registration', '/my-registration/:token', '/reset-password', '/stats/:token',
  '/about-us', '/privacy-policy', '/terms-of-service', '/refund-policy', '/shipping-policy', '/contact-us'];

const EVENT_PATHS = ['/register/:ref/*', '/desk/:ref', '/admin/events/:ref/*', '/team/:ref'];

/** Where a path on an event's custom domain should go instead, or null when it may stay. */
export const domainRedirect = (pathname, site) => {
  const own = [site.eventId, site.slug].filter(Boolean);
  const ref = site.slug || site.eventId;
  for (const pattern of EVENT_PATHS) {
    const match = matchPath(pattern, pathname);
    if (!match) continue;
    if (own.includes(decodeURIComponent(match.params.ref))) return null;
    if (pattern.startsWith('/admin')) return `/admin/events/${ref}/registrations`;
    if (pattern.startsWith('/team')) return '/login';
    return `/register/${ref}`;
  }
  if (SHARED_PATHS.some((pattern) => matchPath(pattern, pathname))) return null;
  if (pathname === '/auth') return '/login';
  if (pathname === '/desk') return `/desk/${ref}`;
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return `/admin/events/${ref}/registrations`;
  return '/';
};

/** On an event's custom domain only that event's pages are served; anything else goes back to it. */
export const DomainScope = ({ children }) => {
  const { status, site } = useCustomDomain();
  const location = useLocation();
  if (status === 'loading') return <Spinner />;
  if (status === 'event') {
    const target = domainRedirect(location.pathname, site);
    if (target && target !== location.pathname) return <Navigate to={target} replace />;
  }
  return children;
};
